import { faPlus, faMultiply, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { Dropdown, Offcanvas, Modal } from "react-bootstrap";
import { objectEqual } from "../../common/functions/objectComparison";
import {
    CreateKeycloakGroup,
    KeycloakGroup,
    UpdateKeycloakGroup,
    UserfulGroup,
} from "../../model/CarbonExternalModels";
import { selectUserGroups, useCarbonSelector } from "../../store/carbonStore";
import { initKeycloakGroup } from "../../store/userManagement/userManagementUtils";

import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { apiCreateUserGroup, apiUpdateUserGroup } from "../../messages/api/gcmUserManagement";
import { UserPermission } from "../../model/CarbonPermissionModels";
import { Button } from "userful-chronos-common-ui/dist";

interface IProps {
    type: "PRIMARY" | "SECONDARY" | "DROPDOWN";
    group?: KeycloakGroup;
    permissions: UserPermission;
}

export default function GroupForm(props: IProps) {
    const [showForm, setShowForm] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [registerChange, setRegisterChange] = useState<boolean>(false);

    const [originalGroup, setOriginalGroup] = useState<KeycloakGroup>(props.group ? props.group : initKeycloakGroup());
    const [currentGroup, setCurrentGroup] = useState<KeycloakGroup>(originalGroup);

    // Data
    const groups: { [id: string]: UserfulGroup } = useCarbonSelector(selectUserGroups);
    const allGroups: KeycloakGroup[] = [];

    for (const property in groups) {
        allGroups.push(groups[property].group);
    }

    const foundChanges = !objectEqual(originalGroup, currentGroup);
    const nameError =
        (allGroups.find((c) => c.name === currentGroup.name) && currentGroup.name !== originalGroup.name) ||
        currentGroup.name.length === 0
            ? "Please make sure the name is unique and is not empty."
            : "";

    useEffect(() => {
        if (props.group) setOriginalGroup(props.group);
    }, [props.group]);

    useEffect(() => {
        setCurrentGroup(originalGroup);
    }, [originalGroup]);

    function handleNameChange(event) {
        setRegisterChange(true);
        setCurrentGroup({ ...currentGroup, name: event.target.value });
    }
    function handleDescriptionChange(event) {
        setRegisterChange(true);
        setCurrentGroup({ ...currentGroup, description: event.target.value });
    }
    const onFormShow = () => {
        setShowForm(true);
    };
    const onFormHide = () => {
        if (foundChanges) {
            setShowConfirmation(true);
        } else {
            setShowForm(false);
            onRestore();
        }
    };
    const onRestore = () => {
        setRegisterChange(false);
        if (props.group) {
            setOriginalGroup(props.group);
            setCurrentGroup(props.group);
        } else {
            setOriginalGroup(initKeycloakGroup());
        }
    };

    const onCreate = () => {
        if (props.group) {
            const updateKeycloakGroup: UpdateKeycloakGroup = {
                id: currentGroup.id,
                displayName: currentGroup.displayName,
                description: currentGroup.description,
                members: currentGroup.members,
                name: currentGroup.name,
                realmRoles: currentGroup.roles,
            };

            // console.warn(updateKeycloakGroup);

            apiUpdateUserGroup(getGlobalStates().keycloak.token, updateKeycloakGroup);
        } else {
            const createKeycloakGroup: CreateKeycloakGroup = {
                name: currentGroup.name,
                description: currentGroup.description,
                displayName: currentGroup.name,
                members: [],
                realmRoles: [],
            };

            apiCreateUserGroup(getGlobalStates().keycloak.token, createKeycloakGroup);
        }
        onRestore();
        setShowForm(false);
    };

    if (!props.permissions.isUserCreator) return null;

    return (
        <>
            {props.type === "PRIMARY" && (
                <button className="createButton" onClick={(e) => onFormShow()}>
                    <FontAwesomeIcon icon={faPlus} />
                    Create Permission Set
                </button>
            )}
            {props.type === "SECONDARY" && (
                <button className="secondaryButton" onClick={(e) => onFormShow()}>
                    Create Permission Set
                </button>
            )}
            {props.type === "DROPDOWN" && (
                <Dropdown.Item onClick={(e) => onFormShow()}>
                    <div className="gcm-icon-dropdown-menu-icon-container">
                        <FontAwesomeIcon icon={faPlus} />
                    </div>
                    <p>Create Permission Set</p>
                </Dropdown.Item>
            )}

            <Offcanvas show={showForm} onHide={onFormHide} placement="end">
                <div className="gcm-form-Body noselect">
                    <div className="gcm-form-Header">
                        <div className="gcm-form-Row">
                            <h1 className="gcm-form-HeaderTitle">
                                {props.group ? "Edit Permission Set" : "Create Permission Set"}
                            </h1>
                            <button className="userfulIconButton" onClick={onFormHide}>
                                <FontAwesomeIcon icon={faMultiply} />
                            </button>
                        </div>
                        <div className="gcm-form-Row">
                            <h3 className="gcm-form-HeaderDescription">
                                Permission set allow you to assign a set of permissions to a group of users.
                            </h3>
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">Permission Set Details</h4>
                            <h5 className="gcm-form-ContentsDescription">Details relating to this permission set.</h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Name</h5>
                                <h6>{currentGroup.name.length}/60</h6>
                            </div>
                            <input
                                className="userfulInputField"
                                placeholder="Enter name here..."
                                type="text"
                                maxLength={60}
                                value={currentGroup.name}
                                onChange={handleNameChange}
                            />
                            {nameError && registerChange && <div className="errorText">{nameError}</div>}
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Description</h5>
                                <h6>{currentGroup.description.length}/200</h6>
                            </div>
                            <textarea
                                className="uTextarea"
                                placeholder="Enter description here..."
                                maxLength={200}
                                value={currentGroup.description}
                                onChange={handleDescriptionChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="gcm-form-Footer">
                    <button className="secondaryUserfulButton" onClick={onFormHide}>
                        Cancel
                    </button>
                    <button
                        className="primaryUserfulButton"
                        onClick={onCreate}
                        disabled={nameError.length > 0 || !foundChanges}
                    >
                        {props.group ? "Save Changes" : "Create"}
                    </button>
                </div>
            </Offcanvas>

            <Modal
                centered
                show={showConfirmation}
                onHide={() => {
                    setShowConfirmation(false);
                }}
            >
                <div className="gcm-pop-up-modal-wrapper">
                    <div className="gcm-pop-up-modal-wrapper-body">
                        <div className="gcm-pop-up-modal-wrapper-head">
                            <div className="gcm-pop-up-modal-wrapper-title">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <h1>Are you sure you want to leave?</h1>
                            </div>
                            <Button
                                variant="secondary"
                                iconOnly
                                onClick={() => {
                                    setShowConfirmation(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faMultiply} />
                            </Button>
                        </div>
                        <p>
                            Are you sure you want to leave without saving your changes? Any unsaved modifications will
                            be lost.
                        </p>
                    </div>
                    <div className="confirmationDialogHorizontalWrapper reversed">
                        <Button
                            variant="danger"
                            onClick={(e) => {
                                setShowForm(false);
                                setShowConfirmation(false);
                                onRestore();
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={(e) => {
                                setShowConfirmation(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
